/**
 * Merchant kiosk config, from the kiosk config API.
 *
 * A SEPARATE slice from `kioskStore`, and the split is deliberate:
 *
 *   kioskStore        session state  — NOT persisted. A kiosk restarting mid-order must
 *                                      come back to IDLE, not resume a stranger's cart.
 *   kioskConfigStore  merchant setup — PERSISTED. The kiosk has to boot and take orders
 *                                      on a restaurant LAN with no internet, which means
 *                                      surviving a restart without reaching the API.
 *
 * redux-persist whitelists are per-slice, so this could not have been one slice with
 * half of it persisted.
 */
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { KioskConfig } from "../../../kiosk/config/types";
import type { KioskDevice } from "../../../kiosk/payment/deviceTypes";

export interface KioskConfigState {
  /**
   * Device is in kiosk mode. Staff turn it on; a customer can never turn it off.
   *
   * It lives HERE, in the persisted slice, because kiosk mode must survive a restart,
   * a crash and an OTA update — a device that reboots into the POS is a device showing
   * a customer the cashier's app. Deliberately a SIBLING of `config`, not a field
   * inside it, so a config refetch cannot clobber the mode.
   *
   * The customer's session is the opposite and lives in `kioskStore`, unpersisted: a
   * restart mid-order must return to IDLE, not resume a stranger's cart.
   */
  enabled: boolean;
  /**
   * The merchant's kiosk configuration id — `kioskConfigurationId` on the branch record
   * (`getBranchData`). It is what `kioskconfiguration/:id/fetch` is asked for, and `config`
   * below is that call's answer. Persisted with the rest, so a kiosk that restarts offline still
   * knows which configuration it is running.
   */
  configurationId: string | null;
  config: KioskConfig | null;
  isFetching: boolean;
  /** Timestamp of the last successful fetch — lets the UI show staleness offline. */
  fetchedAt: number | null;
  error: string | null;

  /**
   * This PANEL's own record, from `kioskdevice/fetch` — which card terminal is attached, which
   * UPI provider to show a QR for. A different API from `config`, kept in the same slice
   * because it has the same lifecycle in every respect that decided this slice's existence:
   * merchant setup, fetched per boot, and PERSISTED, because a kiosk must come back up and
   * take card payments on a restaurant LAN with no internet.
   *
   * Its own `isFetching`/`error` rather than sharing the config's: two APIs fail
   * independently, and a kiosk whose config refreshed but whose device call timed out must
   * not report itself healthy.
   */
  device: KioskDevice | null;
  isFetchingDevice: boolean;
  deviceFetchedAt: number | null;
  deviceError: string | null;
}

const initialState: KioskConfigState = {
  enabled: false,
  configurationId: null,
  config: null,
  isFetching: false,
  fetchedAt: null,
  error: null,
  device: null,
  isFetchingDevice: false,
  deviceFetchedAt: null,
  deviceError: null,
};

export const kioskConfigSlice = createSlice({
  name: "kioskConfig",
  initialState,
  reducers: {
    /**
     * The ONLY way in or out of kiosk mode. Entering is a staff action; leaving is the
     * hidden exit control, never anything a customer or an error path can trigger.
     */
    setKioskEnabled: (state, action: PayloadAction<boolean>) => {
      state.enabled = action.payload;
    },
    /**
     * From the branch record (`getBranchDataFunc`), on every successful `getBranchData` — `null`
     * when the branch names no configuration, which the backend expresses by OMITTING
     * `kioskConfigurationId` (it is set only when `fetchConfigurationForBranch` finds one).
     *
     * A DIFFERENT id — including `null` — also drops `config`: the config on the device belongs
     * to the id it was fetched for, and the merchant has unmapped or remapped this branch, so
     * showing the old one would sell on another configuration's channels. The SAME id keeps it,
     * so the ordinary re-read of the branch record never blanks a working kiosk. The fetch
     * itself is `useKioskConfigFetch`'s, where the kiosk mounts.
     */
    setKioskConfigurationId: (state, action: PayloadAction<string | null>) => {
      const next = action.payload || null;
      if (next !== state.configurationId) {
        state.config = null;
        state.fetchedAt = null;
      }
      state.configurationId = next;
    },
    /**
     * Trigger for `kioskconfiguration/:id/fetch` — the payload is the configuration id.
     * `saga/kioskConfig` watches it, exactly as it watches `fetchKioskDevice`.
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    fetchKioskConfig: (state, _action: PayloadAction<string>) => {
      state.isFetching = true;
      state.error = null;
    },
    fetchKioskConfigSuccess: (
      state,
      action: PayloadAction<{ config: KioskConfig; now: number }>,
    ) => {
      state.config = action.payload.config;
      state.fetchedAt = action.payload.now;
      state.isFetching = false;
      state.error = null;
    },
    /**
     * Keeps the last-known-good config rather than clearing it. A failed refresh must
     * not take a working kiosk offline — the persisted config is the fallback.
     */
    fetchKioskConfigFailed: (state, action: PayloadAction<string>) => {
      state.isFetching = false;
      state.error = action.payload;
    },

    /** Trigger for `kioskdevice/fetch`. */
    fetchKioskDevice: (state) => {
      state.isFetchingDevice = true;
      state.deviceError = null;
    },
    /**
     * `device: null` is an ANSWER, not a failure: `kioskdevice/fetch` returns `null` for a panel
     * nobody has paired, and a panel with no pairing has no payment tiles.
     */
    fetchKioskDeviceSuccess: (
      state,
      action: PayloadAction<{ device: KioskDevice | null; now: number }>,
    ) => {
      state.device = action.payload.device;
      state.deviceFetchedAt = action.payload.now;
      state.isFetchingDevice = false;
      state.deviceError = null;
    },
    /**
     * Keeps the last-known-good device for the same reason the config does — and here the
     * stakes are higher. Clearing it would take a working card terminal off a panel because a
     * refresh call timed out, leaving a queue of customers with only "pay at counter".
     */
    fetchKioskDeviceFailed: (state, action: PayloadAction<string>) => {
      state.isFetchingDevice = false;
      state.deviceError = action.payload;
    },
  },
});

export const {
  setKioskEnabled,
  setKioskConfigurationId,
  fetchKioskConfig,
  fetchKioskConfigSuccess,
  fetchKioskConfigFailed,
  fetchKioskDevice,
  fetchKioskDeviceSuccess,
  fetchKioskDeviceFailed,
} = kioskConfigSlice.actions;

export default kioskConfigSlice.reducer;
